/**
 * /suprimentos/ordens-compra — lista + criar (avulsa ou de req) + drill (registrar entrada).
 */
import React, { useCallback, useEffect, useState } from "react";
import Checkbox from "../../components/Checkbox";
import { Carregando, Vazio } from "./_shared/Estados";
import { FiAlertCircle, FiPackage, FiPlus, FiRefreshCw, FiX } from "react-icons/fi";

import EntityHeader from "../../components/EntityHeader";
import SlideOverPanel from "../../components/SlideOverPanel";
import { useAuth } from "../../context/AuthContext";
import {
  cancelarOC,
  criarOcAvulsa,
  criarOcDeRequisicao,
  listarFornecedores,
  listarOrdensCompra,
  listarProdutoFornecedores,
  listarRequisicoes,
  obterOrdemCompra,
  obterRequisicao,
  registrarEntrada,
} from "../../services/compras";
import { listarDepositos, listarProdutos } from "../../services/estoque";
import { linhasDe } from "../../services/suprimentos";
import { unidadeDefault } from "../../services/unidades";

import {
  fmtData,
  fmtMoeda,
  fmtQtd,
  SITUACAO_OC_LABEL,
  SITUACAO_OC_TONE,
} from "./helpers";
import {
  Block,
  Card,
  DangerButton,
  ErrorMsg,
  Field,
  FieldLabel,
  FieldRow,
  FieldValue,
  Form,
  FormFooter,
  FormGrid,
  IconButton,
  InlineHint,
  Input,
  Pill,
  PrimaryButton,
  SecondaryButton,
  SectionTitle,
  Select,
  Table,
  TableWrapper,
  Textarea,
  TruncCell,
} from "./styles";

function OrdensCompra() {
  const { request, isMaster, hasAllPermissions } = useAuth();
  const [unidade, setUnidade] = useState(null);
  const [produtos, setProdutos] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);
  const [requisicoesAprovadas, setRequisicoesAprovadas] = useState([]);
  const [ocs, setOcs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [filtros, setFiltros] = useState({ situacao: "", fornecedor: "" });
  const [novoModo, setNovoModo] = useState(null); // "avulsa" | "de-requisicao" | null
  const [novo, setNovo] = useState(null);
  const [detalheId, setDetalheId] = useState(null);
  const [detalhe, setDetalhe] = useState(null);
  const [entrada, setEntrada] = useState(null);
  const [depositos, setDepositos] = useState([]);
  // D36: itens da requisição escolhida (OC parcial) e D34: último preço por fornecedor.
  const [itensReq, setItensReq] = useState([]);
  const [precosFornecedor, setPrecosFornecedor] = useState({});

  const podeCriar = isMaster || hasAllPermissions(["compras:criar_oc"]);
  const podeReceber = isMaster || hasAllPermissions(["compras:registrar_entrada"]);

  useEffect(() => {
    unidadeDefault(request).then(setUnidade);
    listarProdutos(request, { pageSize: 500, ativo: true })
      .then((p) => {
        const items = Array.isArray(p) ? p : p?.results || [];
        setProdutos(items);
      })
      .catch(() => setProdutos([]));
    listarDepositos(request, { ativo: true })
      .then((p) => setDepositos(linhasDe(p)))
      .catch(() => setDepositos([]));
    listarFornecedores(request, { pageSize: 500, ativo: true })
      .then((p) => {
        const items = Array.isArray(p) ? p : p?.results || [];
        setFornecedores(items);
      })
      .catch(() => setFornecedores([]));
    listarRequisicoes(request, { pageSize: 100, situacao: "APROVADA" })
      .then((p) => {
        const items = Array.isArray(p) ? p : p?.results || [];
        setRequisicoesAprovadas(items);
      })
      .catch(() => setRequisicoesAprovadas([]));
  }, [request]);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const params = { pageSize: 100 };
      if (filtros.situacao) params.situacao = filtros.situacao;
      if (filtros.fornecedor) params.fornecedor = filtros.fornecedor;
      const payload = await listarOrdensCompra(request, params);
      const items = Array.isArray(payload) ? payload : payload?.results || [];
      setOcs(items);
      setErro(null);
    } catch (err) {
      setErro(err?.message || "Erro ao carregar.");
    } finally {
      setLoading(false);
    }
  }, [request, filtros.situacao, filtros.fornecedor]);

  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    if (!detalheId) { setDetalhe(null); return; }
    obterOrdemCompra(request, detalheId).then(setDetalhe).catch((err) => {
      setErro(err?.message || "Erro ao carregar detalhe.");
    });
  }, [detalheId, request]);

  // Último preço praticado por este fornecedor, por produto (D34) — vira
  // sugestão no valor unitário e no custo da entrada.
  useEffect(() => {
    const fornecedor = novo?.fornecedor || detalhe?.fornecedor;
    if (!fornecedor) { setPrecosFornecedor({}); return; }
    listarProdutoFornecedores(request, { fornecedor })
      .then((p) => setPrecosFornecedor(Object.fromEntries(
        linhasDe(p).map((pf) => [pf.produto, pf.ultimo_preco]),
      )))
      .catch(() => setPrecosFornecedor({}));
  }, [request, novo?.fornecedor, detalhe?.fornecedor]);

  // OC parcial (D36): ao escolher a requisição, mostra os itens abertos
  // para marcar quais entram nesta OC.
  useEffect(() => {
    if (!novo?.requisicao_id) { setItensReq([]); return; }
    obterRequisicao(request, novo.requisicao_id)
      .then((r) => {
        // So item aprovado entra em OC (regra do backend, 2026-09-18).
        const abertos = (r?.itens || []).filter((it) => it.produto && it.situacao === "APROVADO");
        setItensReq(abertos);
        setNovo((p) => (p ? { ...p, itens_sel: abertos.map((it) => it.id) } : p));
      })
      .catch(() => setItensReq([]));
  }, [request, novo?.requisicao_id]);

  const handleAbrirNova = (modo) => {
    setNovoModo(modo);
    if (modo === "avulsa") {
      setNovo({
        fornecedor: "", prazo: "", observacao: "",
        itens: [{ produto_id: "", qtd: "", valor_unitario: "" }],
      });
    } else {
      setNovo({
        requisicao_id: "", fornecedor: "", prazo: "", observacao: "", itens_sel: [],
      });
    }
  };

  const handleAdicionarItem = () => {
    setNovo((p) => ({
      ...p,
      itens: [...(p.itens || []), { produto_id: "", qtd: "", valor_unitario: "" }],
    }));
  };

  const handleRemoverItem = (idx) => {
    setNovo((p) => ({ ...p, itens: p.itens.filter((_, i) => i !== idx) }));
  };

  const handleAlterarItem = (idx, campo, valor) => {
    setNovo((p) => ({
      ...p,
      itens: p.itens.map((it, i) => (i === idx ? { ...it, [campo]: valor } : it)),
    }));
  };

  const handleSalvar = async (e) => {
    e.preventDefault();
    setErro(null);
    try {
      if (novoModo === "avulsa") {
        if (!novo.fornecedor || !novo.itens?.length) {
          setErro("Fornecedor e ao menos 1 item são obrigatórios.");
          return;
        }
        await criarOcAvulsa(request, {
          fornecedor: novo.fornecedor,
          business_unit: unidade?.id,
          prazo_entrega: novo.prazo || null,
          observacao: novo.observacao || "",
          itens: novo.itens
            .filter((it) => it.produto_id && it.qtd)
            .map((it) => ({
              produto_id: it.produto_id,
              qtd: it.qtd,
              valor_unitario: it.valor_unitario || "0",
            })),
        });
      } else {
        if (!novo.requisicao_id || !novo.fornecedor) {
          setErro("Requisição e fornecedor obrigatórios.");
          return;
        }
        if (itensReq.length && !(novo.itens_sel || []).length) {
          setErro("Marque ao menos um item da requisição.");
          return;
        }
        await criarOcDeRequisicao(request, novo.requisicao_id, {
          fornecedor: novo.fornecedor,
          prazo_entrega: novo.prazo || null,
          observacao: novo.observacao || "",
          valores: {},
          // Vazio = todos os abertos (backend); aqui mandamos sempre a seleção.
          itens: novo.itens_sel || [],
        });
      }
      setNovoModo(null);
      setNovo(null);
      await carregar();
    } catch (err) {
      setErro(err?.message || "Erro ao criar OC.");
    }
  };

  const handleRegistrarEntrada = async (e) => {
    e.preventDefault();
    if (!entrada.qtd_recebida) {
      setErro("Informe quantidade recebida.");
      return;
    }
    try {
      await registrarEntrada(request, detalhe.id, {
        item_oc: entrada.item_oc,
        qtd_recebida: entrada.qtd_recebida,
        custo_real: entrada.custo_real || null,
        observacao: entrada.observacao || "",
        deposito: entrada.deposito || null,
      });
      setEntrada(null);
      // Recarrega detalhe + lista
      const fresh = await obterOrdemCompra(request, detalhe.id);
      setDetalhe(fresh);
      await carregar();
    } catch (err) {
      setErro(err?.message || "Erro ao registrar entrada.");
    }
  };

  const handleCancelarOC = async () => {
    if (!detalhe) return;
    const motivo = window.prompt("Motivo do cancelamento (opcional):");
    if (motivo === null) return;
    try {
      await cancelarOC(request, detalhe.id, motivo || "");
      await carregar();
      setDetalheId(null);
    } catch (err) {
      setErro(err?.message || "Erro ao cancelar.");
    }
  };

  return (
    <>
      <EntityHeader
        title="Ordens de Compra"
        subtitle={`${ocs.length} no resultado`}
        primaryAction={
          podeCriar
            ? {
                label: "Nova OC avulsa",
                icon: <FiPlus size={14} />,
                onClick: () => handleAbrirNova("avulsa"),
              }
            : undefined
        }
        actions={[
          ...(podeCriar && requisicoesAprovadas.length > 0 ? [{
            label: "Nova OC de Requisição",
            icon: <FiPlus size={14} />,
            onClick: () => handleAbrirNova("de-requisicao"),
          }] : []),
          { label: "Atualizar", icon: <FiRefreshCw size={14} />, onClick: carregar },
        ]}
      />

      <Card>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <Select
            value={filtros.situacao}
            onChange={(e) => setFiltros((p) => ({ ...p, situacao: e.target.value }))}
          >
            <option value="">Todas situações</option>
            <option value="ABERTA">Aberta</option>
            <option value="PARCIAL">Parcial</option>
            <option value="RECEBIDA">Recebida</option>
            <option value="CANCELADA">Cancelada</option>
          </Select>
          <Select
            value={filtros.fornecedor}
            onChange={(e) => setFiltros((p) => ({ ...p, fornecedor: e.target.value }))}
          >
            <option value="">Todos fornecedores</option>
            {fornecedores.map((f) => (
              <option key={f.id} value={f.id}>{f.nome}</option>
            ))}
          </Select>
        </div>

        {erro && <ErrorMsg><FiAlertCircle size={14} /> {erro}</ErrorMsg>}

        {loading ? (
          <Carregando />
        ) : ocs.length === 0 ? (
          <Vazio titulo="Nenhuma OC encontrada" />
        ) : (
          <TableWrapper>
            <Table $rowsClickable>
              <colgroup>
                <col style={{ width: "16%" }} />
                <col style={{ width: "20%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "16%" }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Número</th>
                  <th>Fornecedor</th>
                  <th>Situação</th>
                  <th>Valor</th>
                  <th>Itens</th>
                  <th>Prazo</th>
                  <th>Origem</th>
                </tr>
              </thead>
              <tbody>
                {ocs.map((o) => (
                  <tr key={o.id} onClick={() => setDetalheId(o.id)} style={{ cursor: "pointer" }}>
                    <td>{o.numero}</td>
                    <td title={o.fornecedor_nome}><TruncCell>{o.fornecedor_nome}</TruncCell></td>
                    <td>
                      <Pill $tone={SITUACAO_OC_TONE[o.situacao]}>{SITUACAO_OC_LABEL[o.situacao]}</Pill>
                    </td>
                    <td>{fmtMoeda(o.valor_total)}</td>
                    <td>{o.qtd_itens}</td>
                    <td>{fmtData(o.prazo_entrega)}</td>
                    <td>{o.requisicao_numero || "Avulsa"}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrapper>
        )}
      </Card>

      {/* Modal: criar */}
      <SlideOverPanel
        open={Boolean(novo)}
        onClose={() => { setNovo(null); setNovoModo(null); }}
        title={novoModo === "avulsa" ? "Nova OC avulsa" : "Nova OC de Requisição"}
        width="min(95vw, 640px)"
      >
        {novo && (
          <Form onSubmit={handleSalvar}>
            {novoModo === "de-requisicao" && (
              <Field>
                Requisição aprovada *
                <Select
                  value={novo.requisicao_id}
                  onChange={(e) => setNovo((p) => ({ ...p, requisicao_id: e.target.value }))}
                  required
                >
                  <option value="">— Selecione —</option>
                  {requisicoesAprovadas.map((r) => (
                    <option key={r.id} value={r.id}>{r.numero} ({r.qtd_itens} itens)</option>
                  ))}
                </Select>
                <InlineHint>
                  Marque os itens que entram nesta OC — o resto continua aberto na requisição (OC parcial).
                </InlineHint>
                {itensReq.length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 6 }}>
                    {itensReq.map((it) => {
                      const marcado = (novo.itens_sel || []).includes(it.id);
                      return (
                        <label key={it.id} style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13 }}>
                          <Checkbox
                            
                            checked={marcado}
                            onChange={() => setNovo((p) => ({
                              ...p,
                              itens_sel: marcado
                                ? (p.itens_sel || []).filter((id) => id !== it.id)
                                : [...(p.itens_sel || []), it.id],
                            }))}
                          />
                          <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {it.produto_codigo} — {it.produto_nome}
                          </span>
                          <span style={{ color: "var(--ink-3)", whiteSpace: "nowrap" }}>
                            ×{fmtQtd(it.qtd_aprovada || it.qtd_solicitada)} {it.produto_unidade}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </Field>
            )}
            <FormGrid>
              <Field>
                Fornecedor *
                <Select
                  value={novo.fornecedor}
                  onChange={(e) => setNovo((p) => ({ ...p, fornecedor: e.target.value }))}
                  required
                >
                  <option value="">— Selecione —</option>
                  {fornecedores.map((f) => (
                    <option key={f.id} value={f.id}>{f.nome}</option>
                  ))}
                </Select>
              </Field>
              <Field>
                Prazo de entrega
                <Input
                  type="date"
                  value={novo.prazo}
                  onChange={(e) => setNovo((p) => ({ ...p, prazo: e.target.value }))}
                />
              </Field>
            </FormGrid>
            <Field>
              Observação
              <Textarea
                value={novo.observacao}
                onChange={(e) => setNovo((p) => ({ ...p, observacao: e.target.value }))}
                rows={2}
              />
            </Field>

            {novoModo === "avulsa" && (
              <Block>
                <SectionTitle>Itens</SectionTitle>
                {novo.itens.map((it, idx) => (
                  <FieldRow key={idx} style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr auto", gap: 8 }}>
                    <Select
                      value={it.produto_id}
                      onChange={(e) => handleAlterarItem(idx, "produto_id", e.target.value)}
                      required
                    >
                      <option value="">— Produto —</option>
                      {produtos.map((p) => (
                        <option key={p.id} value={p.id}>{p.codigo} — {p.nome} ({p.unidade}){Number(p.custo_medio) > 0 ? "" : " · sem preço"}</option>
                      ))}
                    </Select>
                    <Input
                      type="number"
                      step="0.001"
                      min="0.001"
                      placeholder="Qtd"
                      value={it.qtd}
                      onChange={(e) => handleAlterarItem(idx, "qtd", e.target.value)}
                      required
                    />
                    <Input
                      type="number"
                      step="0.0001"
                      min="0"
                      placeholder={precosFornecedor[it.produto_id] ? `último: ${precosFornecedor[it.produto_id]}` : "Vlr unit"}
                      title={precosFornecedor[it.produto_id] ? `Último preço deste fornecedor: ${fmtMoeda(precosFornecedor[it.produto_id])}` : undefined}
                      value={it.valor_unitario}
                      onChange={(e) => handleAlterarItem(idx, "valor_unitario", e.target.value)}
                    />
                    <IconButton type="button" onClick={() => handleRemoverItem(idx)} title="Remover">
                      <FiX size={11} />
                    </IconButton>
                  </FieldRow>
                ))}
                <SecondaryButton type="button" onClick={handleAdicionarItem} style={{ marginTop: 8 }}>
                  <FiPlus size={12} /> Adicionar item
                </SecondaryButton>
              </Block>
            )}

            <FormFooter>
              <SecondaryButton type="button" onClick={() => { setNovo(null); setNovoModo(null); }}>
                Cancelar
              </SecondaryButton>
              <PrimaryButton type="submit">Criar OC</PrimaryButton>
            </FormFooter>
          </Form>
        )}
      </SlideOverPanel>

      {/* Modal: detalhe + entradas */}
      <SlideOverPanel
        open={Boolean(detalheId)}
        onClose={() => setDetalheId(null)}
        title={detalhe?.numero || "Carregando..."}
        subtitle={detalhe && `${SITUACAO_OC_LABEL[detalhe.situacao]} — ${detalhe.fornecedor_nome}`}
        width="min(95vw, 620px)"
      >
        {!detalhe ? (
          <Carregando />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Block>
              <SectionTitle>Resumo</SectionTitle>
              <FieldRow>
                <FieldLabel>Situação</FieldLabel>
                <FieldValue>
                  <Pill $tone={SITUACAO_OC_TONE[detalhe.situacao]}>{SITUACAO_OC_LABEL[detalhe.situacao]}</Pill>
                </FieldValue>
              </FieldRow>
              <FieldRow>
                <FieldLabel>Fornecedor</FieldLabel>
                <FieldValue>{detalhe.fornecedor_nome}</FieldValue>
              </FieldRow>
              <FieldRow>
                <FieldLabel>Valor total</FieldLabel>
                <FieldValue><strong>{fmtMoeda(detalhe.valor_total)}</strong></FieldValue>
              </FieldRow>
              <FieldRow>
                <FieldLabel>Prazo</FieldLabel>
                <FieldValue>{fmtData(detalhe.prazo_entrega)}</FieldValue>
              </FieldRow>
              {detalhe.requisicao_numero && (
                <FieldRow>
                  <FieldLabel>Origem</FieldLabel>
                  <FieldValue>{detalhe.requisicao_numero}</FieldValue>
                </FieldRow>
              )}
            </Block>

            <Block>
              <SectionTitle>Itens ({detalhe.itens?.length || 0})</SectionTitle>
              <TableWrapper>
                <Table>
                  <thead>
                    <tr>
                      <th>Produto</th>
                      <th>Qtd</th>
                      <th>Recebida</th>
                      <th>Subt</th>
                      <th>Ação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detalhe.itens?.map((it) => {
                      const pendente = Number(it.qtd) - Number(it.qtd_recebida || 0);
                      const podeReceberItem = pendente > 0 && ["ABERTA", "PARCIAL"].includes(detalhe.situacao);
                      return (
                        <tr key={it.id}>
                          <td title={`${it.produto_codigo} — ${it.produto_nome}`}>
                            <TruncCell>{it.produto_codigo}</TruncCell>
                          </td>
                          <td>{fmtQtd(it.qtd)} {it.produto_unidade}</td>
                          <td>{fmtQtd(it.qtd_recebida)}</td>
                          <td>{fmtMoeda(it.subtotal)}</td>
                          <td>
                            {podeReceber && podeReceberItem && (
                              <IconButton
                                type="button"
                                onClick={() => setEntrada({
                                  item_oc: it.id, produto: it.produto_codigo, produto_id: it.produto,
                                  deposito: "",
                                  qtd_recebida: String(pendente),
                                  custo_real: it.valor_unitario,
                                  observacao: "",
                                  pendente,
                                })}
                                title="Registrar entrada"
                              >
                                <FiPackage size={11} />
                              </IconButton>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </TableWrapper>
            </Block>

            {detalhe.entradas?.length > 0 && (
              <Block>
                <SectionTitle>Entradas ({detalhe.entradas.length})</SectionTitle>
                {detalhe.entradas.map((e) => (
                  <FieldRow key={e.id}>
                    <FieldLabel>{fmtData(e.created_at)}</FieldLabel>
                    <FieldValue>
                      {fmtQtd(e.qtd_recebida)} de {e.produto_codigo} a {fmtMoeda(e.custo_real)} (por {e.criado_por_nome})
                    </FieldValue>
                  </FieldRow>
                ))}
              </Block>
            )}

            {detalhe.situacao === "ABERTA" && podeCriar && (
              <DangerButton type="button" onClick={handleCancelarOC}>
                <FiX size={12} /> Cancelar OC
              </DangerButton>
            )}
          </div>
        )}
      </SlideOverPanel>

      {/* Modal: registrar entrada */}
      <SlideOverPanel
        open={Boolean(entrada)}
        onClose={() => setEntrada(null)}
        title="Registrar entrada"
        subtitle={entrada && `${entrada.produto} — pendente ${entrada.pendente}`}
        width="min(90vw, 440px)"
      >
        {entrada && (
          <Form onSubmit={handleRegistrarEntrada}>
            <Field>
              Quantidade recebida *
              <Input
                type="number"
                step="0.001"
                min="0.001"
                value={entrada.qtd_recebida}
                onChange={(e) => setEntrada((p) => ({ ...p, qtd_recebida: e.target.value }))}
                required
                autoFocus
              />
              <InlineHint>Pendente: {entrada.pendente}</InlineHint>
            </Field>
            <Field>
              Custo real (R$ por unidade)
              <Input
                type="number"
                step="0.0001"
                min="0"
                value={entrada.custo_real}
                onChange={(e) => setEntrada((p) => ({ ...p, custo_real: e.target.value }))}
              />
              <InlineHint>
                Atualiza custo médio do produto (média ponderada) e o último preço deste fornecedor.
                {precosFornecedor[entrada.produto_id] ? ` Último: ${fmtMoeda(precosFornecedor[entrada.produto_id])}.` : ""}
              </InlineHint>
            </Field>
            <Field>
              Depósito de entrada
              <Select
                value={entrada.deposito || ""}
                onChange={(e) => setEntrada((p) => ({ ...p, deposito: e.target.value }))}
              >
                <option value="">— Depósito de recebimento (configuração) —</option>
                {depositos.map((d) => <option key={d.id} value={d.id}>{d.nome}</option>)}
              </Select>
            </Field>
            <Field>
              Observação
              <Textarea
                value={entrada.observacao}
                onChange={(e) => setEntrada((p) => ({ ...p, observacao: e.target.value }))}
                rows={2}
                placeholder="Ex: nota fiscal X, divergência de cor, etc"
              />
            </Field>
            <FormFooter>
              <SecondaryButton type="button" onClick={() => setEntrada(null)}>Cancelar</SecondaryButton>
              <PrimaryButton type="submit">Registrar</PrimaryButton>
            </FormFooter>
          </Form>
        )}
      </SlideOverPanel>
    </>
  );
}

export default OrdensCompra;
